import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    Button,
    Card,
    CardBody,
    Chip,
    Skeleton,
    Autocomplete,
    AutocompleteItem,
    Input,
} from "@heroui/react";
import { Filter, BookOpen, Eye, X, Sparkles, MessageSquare, List, Upload, ChevronDown, Verified, Search } from "lucide-react";
import { useGetPublicPascos } from "@src/presentation/hooks/pasco/useGetPublicPascos";
import { useSearchPublicPascos } from "@src/presentation/hooks/pasco/useSearchPublicPascos";
import { useGetPublicPascoFilterOptions } from "@src/presentation/hooks/pasco/useGetPublicPascoFilterOptions";
import { useUniversities } from "@src/presentation/hooks/universities";
import { useInView } from "react-intersection-observer";
import { useEffect, useRef } from "react";
import ChatInterfaceRevamped from "@src/presentation/components/ChatInterfaceRevamped";
import ChatDocumentsViewer from "@src/presentation/components/ChatDocumentsViewer";
import { PascoDetailModal } from "@src/presentation/components/PascoDetailModal";
import { FilterModal } from "@src/presentation/components/FilterModal";
import type { FilterState } from "@src/presentation/components/FilterModal";
import { useChat } from "@src/presentation/contexts/ChatContext";
import { useAuth } from "@src/presentation/contexts/AuthContext";
import { useMobileView } from "@src/presentation/contexts/MobileViewContext";
import useScreenSize, { ScreenSize } from "@src/presentation/hooks/useScreenSize";
import { useAnalytics } from "@src/presentation/hooks/analytics";
import { WhatsAppLinkBanner } from "@src/presentation/components/WhatsAppLinkBanner";

interface PascoDisplay {
    pascoId: string;
    courseCode: string;
    courseName: string;
    academicYear: string;
    semester: number;
    department?: string;
    level: number;
    isInterimAssessment: boolean;
    isSupplementary: boolean;
    isReviewed: boolean;
}

export default function PublicHomePageRevamped() {
    const navigate = useNavigate();
    const { selectedMessageId } = useChat();
    const { isAuthenticated, user } = useAuth();
    const { mobileView, setMobileView } = useMobileView();
    const screenSize = useScreenSize();
    const analytics = useAnalytics();

    // Modal state
    const [selectedPascoId, setSelectedPascoId] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Filter modal state - only used on mobile
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

    // Handle pasco click - open modal on mobile, navigate on desktop
    const handlePascoClick = (pascoId: string) => {
        const pasco = displayPascos.find(p => p.pascoId === pascoId);

        // Track pasco click
        analytics.trackPascoClick(pascoId, screenSize === ScreenSize.Mobile ? 'open_modal' : 'navigate', {
            pasco_title: pasco ? `${pasco.courseCode || 'Unknown'} - ${pasco.courseName || 'Untitled'}` : 'unknown',
            course_code: pasco?.courseCode,
            page: 'public_home',
            view_type: screenSize === ScreenSize.Mobile ? 'mobile_modal' : 'desktop_page',
        });

        if (screenSize === ScreenSize.Mobile) {
            setSelectedPascoId(pascoId);
            setIsModalOpen(true);
        } else {
            navigate(`/pascos/${pascoId}`);
        }
    };

    // Handle modal close
    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedPascoId(null);
    };

    // Search and Filters
    const [searchQuery, setSearchQuery] = useState<string>("");
    // Filters - Only categorical metadata (no course code - that's handled by search)
    const [semesterFilter, setSemesterFilter] = useState<string>("");
    const [academicYearFilter, setAcademicYearFilter] = useState<string>("");
    const [universityFilter, setUniversityFilter] = useState<string>("");

    // Mobile detection - keeping for potential future use
    // const [isMobile, setIsMobile] = useState(false);

    // Fetch universities for filter
    const { data: universitiesData, isLoading: isLoadingUniversities } = useUniversities();

    // Fetch filter options
    const { data: filterOptionsData } = useGetPublicPascoFilterOptions();

    // Intersection observer for infinite scroll
    const { ref: infiniteScrollRef, inView } = useInView({
        threshold: 0.1,
        rootMargin: '100px',
    });

    // Build public pasco params (filtering without search)
    const publicPascoParams = useMemo(
        () => ({
            universityId: isAuthenticated ? user?.universityId || undefined : universityFilter || undefined,
            semester: semesterFilter || undefined,
            academicYear: academicYearFilter || undefined,
            limit: 20,
        }),
        [isAuthenticated, user?.universityId, universityFilter, semesterFilter, academicYearFilter]
    );

    // Build search params - includes filters for refined search
    const searchPascoParams = useMemo(
        () => ({
            query: searchQuery.trim(),
            universityId: isAuthenticated ? user?.universityId || undefined : universityFilter || undefined,
            semester: semesterFilter || undefined,
            academicYear: academicYearFilter || undefined,
            limit: 20,
        }),
        [searchQuery, isAuthenticated, user?.universityId, universityFilter, semesterFilter, academicYearFilter]
    );

    // Determine if we should use search or regular browsing
    const isSearching = searchQuery.trim().length > 0;

    // Fetch paginated PASCOs - use search or regular based on query
    const publicPascosQuery = useGetPublicPascos(publicPascoParams);
    const searchPascosQuery = useSearchPublicPascos(searchPascoParams);

    // Use the appropriate query based on whether we're searching
    const activeQuery = isSearching ? searchPascosQuery : publicPascosQuery;

    // Use the appropriate query based on authentication
    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
        isError,
        error,
    } = activeQuery;

    // Convert data to display format
    const defaultPascos = useMemo((): PascoDisplay[] => {
        if (!data?.pages) return [];
        return data.pages.flatMap((page: any) => page?.pascos || []);
    }, [data]);

    // Get total count from the API response (filtered results)
    const totalPascos = data?.pages[0]?.total || 0;

    // Determine which PASCOs to display
    // Apply sorting for easier viewing
    const displayPascos = useMemo(() => {
        let filtered = defaultPascos;

        // Sort by course name for easier viewing
        // filtered = filtered.sort((a, b) => a.courseName.localeCompare(b.courseName));

        return filtered;
    }, [defaultPascos]);

    // Extract unique filter options from API
    const filterOptions = useMemo(() => {
        if (!filterOptionsData) {
            return {
                courseCodes: [],
                semesters: [],
                academicYears: [],
            };
        }

        return {
            courseCodes: filterOptionsData.course_codes.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })),
            semesters: filterOptionsData.semesters.map(s => s.toString()).sort(),
            academicYears: filterOptionsData.academic_years.sort(),
        };
    }, [filterOptionsData]);

    // Load more when in view (only for default results)
    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    // Scroll to top when filters are applied (not including search)
    useEffect(() => {
        const hasActiveFilters = semesterFilter || academicYearFilter || universityFilter;
        if (hasActiveFilters && scrollRef.current) {
            scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [semesterFilter, academicYearFilter, universityFilter]);

    const scrollRef = useRef<HTMLDivElement>(null);

    const clearFilters = () => {
        setSearchQuery("");
        setSemesterFilter("");
        setAcademicYearFilter("");
        setUniversityFilter("");
    };

    const applyFilters = (filters: FilterState) => {
        const filtersApplied = [];
        if (filters.searchQuery) filtersApplied.push('search_query');
        if (filters.semester) filtersApplied.push('semester');
        if (filters.academicYear) filtersApplied.push('academic_year');
        if (filters.university) filtersApplied.push('university');

        if (filtersApplied.length > 0) {
            analytics.trackEvent({
                category: 'Filter',
                action: 'apply',
                label: 'public_home_filters',
                metadata: {
                    filters_applied: filtersApplied.join(','),
                    search_query: filters.searchQuery,
                    semester: filters.semester,
                    academic_year: filters.academicYear,
                    university: filters.university,
                },
            });
        }

        setSearchQuery(filters.searchQuery || "");
        setSemesterFilter(filters.semester);
        setAcademicYearFilter(filters.academicYear);
        setUniversityFilter(filters.university);
    };

    const hasActiveFilters =
        searchQuery ||
        semesterFilter ||
        academicYearFilter ||
        universityFilter;

    return (
        <div className="h-full bg-background overflow-hidden flex flex-col">
            {/* Mobile View Toggle Buttons - Placed above chat area on mobile */}
            <div className="lg:hidden w-full flex justify-center gap-2 py-2 bg-background border-b border-border sticky top-0 z-30">
                <Button
                    size="sm"
                    color={mobileView === 'browse' ? 'primary' : 'default'}
                    variant={mobileView === 'browse' ? 'solid' : 'light'}
                    onPress={() => {
                        analytics.trackButtonClick('mobile-view-browse', 'public_home_mobile_toggle');
                        setMobileView('browse');
                    }}
                    startContent={<List className="h-4 w-4" />}
                    className="rounded-full"
                >
                    Browse
                </Button>
                <Button
                    size="sm"
                    color={mobileView === 'chat' ? 'primary' : 'default'}
                    variant={mobileView === 'chat' ? 'solid' : 'light'}
                    onPress={() => {
                        analytics.trackButtonClick('mobile-view-chat', 'public_home_mobile_toggle');
                        analytics.trackPepperBotUsage('chat_view_opened', { source: 'mobile_toggle' });
                        setMobileView('chat');
                    }}
                    startContent={<MessageSquare className="h-4 w-4" />}
                    className="rounded-full"
                >
                    Chat
                </Button>
            </div>

            {/* Mobile: Full Screen Chat View */}
            {mobileView === 'chat' && (
                <div className="lg:hidden h-full overflow-hidden">
                    <div className="h-full px-4">
                        <ChatInterfaceRevamped />
                    </div>
                </div>
            )}

            {/* Mobile: Browse View & Desktop Layout */}
            <div className={`${mobileView === 'browse' ? 'block' : 'hidden'} lg:block flex-1 overflow-hidden`}>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-full">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full">
                        {/* Main Content Area - Left 2/3 - Scrollable */}
                        <div className="lg:col-span-2 flex flex-col h-full overflow-hidden">
                            <div ref={scrollRef} className="flex-1 overflow-y-auto pr-3">
                                {/* Header - Scrollable (will scroll out of view) */}
                                <div className="pt-6 pb-4">
                                    <div className="flex items-center gap-2">
                                        {isAuthenticated && user ? (
                                            <p className="text-sm text-foreground/60">
                                                Welcome back, <span className="font-semibold text-primary">{user.firstName || user.displayName || 'there'}</span>!
                                            </p>
                                        ) : (
                                            <p className="text-sm text-foreground/60">
                                                Welcome to <span className="font-semibold text-primary">PascoHub</span>!
                                            </p>
                                        )}
                                    </div>
                                    <p className="flex items-center gap-1 text-sm md:text-md font-medium text-foreground">
                                        Browse and search <span className="text-primary font-semibold">PASCOs</span> with{" "}
                                        <span className="inline-flex items-center gap-1 text-primary font-semibold">
                                            <Sparkles className="h-4 w-4" />
                                            AI assistance
                                        </span>
                                    </p>
                                </div>

                                {/* Upload CTA - Encourage users to contribute */}
                                <Card className="border border-primary/20 bg-gradient-to-r from-primary/5 to-blue-50/50 dark:to-card rounded-none shadow-none mb-6">
                                    <CardBody className="p-4">
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1">
                                                <h3 className="text-sm font-semibold text-primary mb-1">
                                                    Have pascos to share?
                                                </h3>
                                                <p className="text-xs text-default-600">
                                                    Upload past questions. Earn credits. Help thousands of students.
                                                </p>
                                            </div>
                                            <Button
                                                size="sm"
                                                color="primary"
                                                variant="solid"
                                                onPress={() => {
                                                    analytics.trackButtonClick('upload-pasco-cta', 'public_home_banner');
                                                    navigate("/upload");
                                                }}
                                                startContent={<Upload className="h-3 w-3" />}
                                                className="rounded ml-4 flex-shrink-0"
                                            >
                                                Upload
                                            </Button>
                                        </div>
                                    </CardBody>
                                </Card>

                                {/* WhatsApp Link CTA - Show for mobile unlinked users */}
                                {screenSize === ScreenSize.Mobile && (
                                    <div className="mb-6">
                                        <WhatsAppLinkBanner
                                            variant="prominent"
                                            dismissible
                                            dismissKey="home-whatsapp-banner-dismissed"
                                        />
                                    </div>
                                )}

                                {/* Filters Bar - Sticky at top of scroll container */}
                                <div className="sticky top-0 z-20 pb-4">
                                    {screenSize === ScreenSize.Mobile ? (
                                        // Mobile: Search field prominent, filter button separate
                                        <div className="space-y-0 bg-card backdrop-blur-xl supports-[backdrop-filter]:bg-background/60 shadow-none hover:shadow-custom transition-shadow duration-200 rounded">
                                            {/* Search Input - Primary, always visible */}
                                            <Input
                                                type="text"
                                                placeholder="Search by course name or code..."
                                                value={searchQuery}
                                                onValueChange={setSearchQuery}
                                                startContent={<Search className="h-4 w-4 text-foreground/60" />}
                                                isClearable
                                                onClear={() => setSearchQuery("")}
                                                radius="none"
                                                classNames={{
                                                    base: 'focus-within:ring-1 focus-within:ring-primary placeholder:text-xs',
                                                    inputWrapper: 'border border-border bg-background shadow-none h-11 rounded',

                                                }}
                                            />

                                            {/* Filter controls row */}
                                            <div className="flex items-center justify-between py-1">
                                                <div className="flex items-center gap-2">
                                                    <Button
                                                        size="sm"
                                                        variant="flat"
                                                        onPress={() => setIsFilterModalOpen(true)}
                                                        startContent={<Filter className="h-4 w-4" />}
                                                        className="rounded"
                                                        color={hasActiveFilters && !searchQuery ? "primary" : hasActiveFilters ? "primary" : "default"}
                                                    >
                                                        {hasActiveFilters && !searchQuery ? `Filters (${[semesterFilter, academicYearFilter, universityFilter].filter(Boolean).length})` : "Filters"}
                                                    </Button>
                                                    {hasActiveFilters && (
                                                        <Button
                                                            size="sm"
                                                            variant="light"
                                                            color="danger"
                                                            onPress={clearFilters}
                                                            startContent={<X className="h-3 w-3" />}
                                                            className="rounded"
                                                        >
                                                            Clear All
                                                        </Button>
                                                    )}
                                                </div>
                                                <Chip size="sm" variant="flat" color="primary" endContent={isFetchingNextPage ? <div className="animate-spin h-3 w-3 border border-current border-t-transparent rounded-full" /> : hasNextPage ? <ChevronDown className="h-3 w-3 opacity-70" /> : null}>
                                                    {isFetchingNextPage ? 'Loading...' : hasNextPage ? `${displayPascos.length}+` : `${displayPascos.length}`}
                                                </Chip>
                                            </div>
                                        </div>
                                    ) : (
                                        // Desktop: Compact horizontal filter layout
                                        <Card className="border border-border bg-card backdrop-blur-xl supports-[backdrop-filter]:bg-background/60 shadow-none hover:shadow-custom transition-shadow duration-200 rounded">
                                            <CardBody className="px-3 pb-2 pt-1.5">
                                                {/* Top row: Title, Count, Clear */}
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="flex items-center justify-between w-full">
                                                        <div className="flex items-center gap-2">
                                                            <Filter className="h-4 w-4 text-foreground/60" />
                                                            <h3 className="text-sm font-medium">Filters</h3>
                                                        </div>
                                                        <Chip size="sm" variant="flat" color="primary" className="h-6">
                                                            {isFetchingNextPage ? 'Loading...' : hasNextPage ? `${displayPascos.length}+` : `${displayPascos.length}`}
                                                        </Chip>
                                                    </div>
                                                    {hasActiveFilters && (
                                                        <Button
                                                            size="sm"
                                                            variant="light"
                                                            color="danger"
                                                            onPress={clearFilters}
                                                            startContent={<X className="h-3 w-3" />}
                                                            className="rounded h-8"
                                                        >
                                                            Clear All
                                                        </Button>
                                                    )}
                                                </div>

                                                {/* Horizontal filter row */}
                                                <div className="flex items-end gap-2">
                                                    {/* Search Input - Takes more space */}
                                                    <div className="flex-1 min-w-0">
                                                        <Input
                                                            type="text"
                                                            size="sm"
                                                            placeholder="Search by course name or code..."
                                                            value={searchQuery}
                                                            onValueChange={setSearchQuery}
                                                            startContent={<Search className="h-3.5 w-3.5 text-foreground/60" />}
                                                            isClearable
                                                            onClear={() => setSearchQuery("")}
                                                            radius="none"
                                                            classNames={{
                                                                base: 'focus-within:ring-2 focus-within:ring-primary placeholder:text-xs',
                                                                inputWrapper: 'border border-border h-12 shadow-none',
                                                                input: 'text-xs',
                                                            }}
                                                        />
                                                    </div>

                                                    {/* Semester Filter - Compact */}
                                                    <div className="w-36">
                                                        <Autocomplete
                                                            size="sm"
                                                            label="Semester"
                                                            radius='none'
                                                            placeholder="All"
                                                            selectedKey={semesterFilter}
                                                            onSelectionChange={(key) => setSemesterFilter(key as string)}
                                                            classNames={{
                                                                popoverContent: 'rounded border border-border',
                                                                base: 'focus-within:ring-2 focus-within:ring-primary border border-border',
                                                                selectorButton: 'h-10',
                                                            }}
                                                        >
                                                            {filterOptions.semesters.map((semester) => (
                                                                <AutocompleteItem className="rounded" key={semester}>
                                                                    {semester === "1" ? "1st" : semester === "2" ? "2nd" : semester === "3" ? "3rd" : semester}
                                                                </AutocompleteItem>
                                                            ))}
                                                        </Autocomplete>
                                                    </div>

                                                    {/* Academic Year Filter - Compact */}
                                                    <div className="w-40">
                                                        <Autocomplete
                                                            size="sm"
                                                            label="Year"
                                                            placeholder="All"
                                                            radius="none"
                                                            selectedKey={academicYearFilter}
                                                            onSelectionChange={(key) => setAcademicYearFilter(key as string)}
                                                            classNames={{
                                                                popoverContent: 'rounded border border-border',
                                                                base: 'focus-within:ring-2 focus-within:ring-primary border border-border',
                                                                selectorButton: 'h-10',
                                                            }}
                                                        >
                                                            {filterOptions.academicYears.map((year) => (
                                                                <AutocompleteItem className="rounded" key={year}>{year}</AutocompleteItem>
                                                            ))}
                                                        </Autocomplete>
                                                    </div>

                                                    {/* University Filter - Only for unauthenticated users */}
                                                    {!isAuthenticated && (
                                                        <div className="w-44">
                                                            <Autocomplete
                                                                size="sm"
                                                                label="University"
                                                                placeholder="All"
                                                                radius="none"
                                                                selectedKey={universityFilter}
                                                                onSelectionChange={(key) => setUniversityFilter(key as string)}
                                                                isLoading={isLoadingUniversities}
                                                                classNames={{
                                                                    popoverContent: 'rounded border border-border',
                                                                    base: 'focus-within:ring-2 focus-within:ring-primary border border-border',
                                                                    selectorButton: 'h-10',
                                                                }}
                                                            >
                                                                {(universitiesData?.universities || []).map((university) => (
                                                                    <AutocompleteItem className="rounded" key={university.university_id}>
                                                                        {university.code}
                                                                    </AutocompleteItem>
                                                                ))}
                                                            </Autocomplete>
                                                        </div>
                                                    )}
                                                </div>
                                            </CardBody>
                                        </Card>
                                    )}
                                </div>

                                {/* Scrollable Content Area */}
                                <div className="space-y-6 pb-6">
                                    {/* Chat Documents Viewer - Shows when a message is selected */}
                                    {selectedMessageId && (
                                        <div className="mt-4">
                                            <ChatDocumentsViewer />
                                        </div>
                                    )}

                                    {/* Results Header */}
                                    <div className="flex items-center justify-between pt-4">
                                        <div className="flex items-center gap-3">
                                            <h2 className="text-base font-medium text-foreground/80">
                                                Documents
                                            </h2>
                                        </div>
                                    </div>

                                    {/* Loading State */}
                                    {isLoading && (
                                        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
                                            {[...Array(6)].map((_, i) => (
                                                <Card key={i} className="border border-border rounded shadow-none">
                                                    <CardBody className="p-4 space-y-3">
                                                        <Skeleton className="h-5 w-3/4 rounded" />
                                                        <Skeleton className="h-4 w-1/2 rounded" />
                                                        <div className="flex gap-2">
                                                            <Skeleton className="h-6 w-16 rounded-full" />
                                                            <Skeleton className="h-6 w-20 rounded-full" />
                                                        </div>
                                                        <Skeleton className="h-9 w-full rounded" />
                                                    </CardBody>
                                                </Card>
                                            ))}
                                        </div>
                                    )}

                                    {/* Error State */}
                                    {isError && (
                                        <Card className="border border-danger/20 rounded shadow-none bg-danger/5">
                                            <CardBody className="p-6 text-center">
                                                <p className="text-danger">
                                                    Error loading documents: {error?.message || "Unknown error"}
                                                </p>
                                            </CardBody>
                                        </Card>
                                    )}

                                    {/* Results Grid */}
                                    {!isLoading && !isError && (
                                        <>
                                            {displayPascos.length === 0 ? (
                                                <Card className="border border-border rounded shadow-none">
                                                    <CardBody className="text-center py-12">
                                                        <BookOpen className="h-12 w-12 mx-auto mb-4 text-foreground/30" />
                                                        <h3 className="text-base font-semibold mb-2">
                                                            No documents found
                                                        </h3>
                                                        <p className="text-sm text-foreground/60">
                                                            No documents match your current filters
                                                        </p>
                                                    </CardBody>
                                                </Card>
                                            ) : (
                                                <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:mb-0">
                                                    {displayPascos.map((pasco) => (
                                                        <Card
                                                            key={pasco.pascoId}
                                                            isPressable
                                                            onPress={() => handlePascoClick(pasco.pascoId)}
                                                            className="border border-border rounded shadow-none hover:border-primary/50 transition-all duration-200 hover:shadow-custom"
                                                        >

                                                            <CardBody className="p-4">
                                                                <div className="space-y-3">
                                                                    <div>
                                                                        <div className="flex items-center gap-2 mb-1 justify-between">
                                                                            <h3 className="font-semibold text-sm line-clamp-2">
                                                                                {pasco.courseName || 'Untitled PASCO'}
                                                                            </h3>
                                                                            {pasco.isReviewed && (
                                                                                <div className="flex items-center gap-1 bg-success/10 text-success px-0 py-0 sm:px-2 sm:py-0.5 rounded-full text-xs font-medium">
                                                                                    <span><Verified className="w-3.5 h-3.5" /></span>
                                                                                    <span className="hidden sm:inline">Reviewed</span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <p className="text-xs text-primary font-medium">
                                                                            {pasco.courseCode || 'No course code'}
                                                                        </p>
                                                                    </div>

                                                                    <div className="flex flex-wrap gap-1.5">
                                                                        {pasco.level && (
                                                                            <Chip size="sm" variant="flat">
                                                                                Level {pasco.level}
                                                                            </Chip>
                                                                        )}
                                                                        {pasco.semester && (
                                                                            <Chip size="sm" variant="flat" color="primary">
                                                                                Sem {pasco.semester}
                                                                            </Chip>
                                                                        )}
                                                                        {!pasco.isReviewed && (
                                                                            <Chip size="sm" variant="flat" color="secondary">
                                                                                🤖 AI Processed
                                                                            </Chip>
                                                                        )}
                                                                        {pasco.isInterimAssessment && (
                                                                            <Chip size="sm" variant="flat" color="secondary">
                                                                                Interim
                                                                            </Chip>
                                                                        )}
                                                                        {pasco.isSupplementary && (
                                                                            <Chip size="sm" variant="flat" color="warning">
                                                                                Supplementary
                                                                            </Chip>
                                                                        )}
                                                                    </div>

                                                                    <div className="text-xs text-foreground/60 space-y-0.5">
                                                                        {pasco.department && (
                                                                            <p className="line-clamp-1">{pasco.department}</p>
                                                                        )}
                                                                        {pasco.academicYear && <p>{pasco.academicYear}</p>}
                                                                    </div>

                                                                    <div className="bg-primary/20 hover:bg-primary/18 transition-colors py-2 flex gap-2 items-center justify-center text-xs text-primary font-medium cursor-pointer select-none">
                                                                        <Eye className="h-3 w-3" />

                                                                        View Details
                                                                    </div>
                                                                </div>
                                                            </CardBody>
                                                        </Card>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Infinite Scroll Trigger - Always rendered for proper intersection detection */}
                                            <div ref={infiniteScrollRef} className="h-1" />

                                            {/* Loading skeletons when fetching next page */}
                                            {isFetchingNextPage && (
                                                <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
                                                    {[...Array(2)].map((_, i) => (
                                                        <Card key={i} className="border border-border rounded shadow-none">
                                                            <CardBody className="p-4 space-y-3">
                                                                <Skeleton className="h-5 w-3/4 rounded" />
                                                                <Skeleton className="h-4 w-1/2 rounded" />
                                                                <div className="flex gap-2">
                                                                    <Skeleton className="h-6 w-16 rounded-full" />
                                                                    <Skeleton className="h-6 w-20 rounded-full" />
                                                                </div>
                                                                <Skeleton className="h-9 w-full rounded" />
                                                            </CardBody>
                                                        </Card>
                                                    ))}
                                                </div>
                                            )}

                                            {!hasNextPage && displayPascos.length > 0 && (
                                                <p className="text-center text-sm text-foreground/60 py-6">
                                                    You've reached the end
                                                </p>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Desktop: Chat Interface - Right 1/3 - Fixed, No Scroll */}
                        <div className="hidden lg:block lg:col-span-1 max-h-[90vh]">
                            <div className="h-full py-6">
                                <ChatInterfaceRevamped />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Pasco Detail Modal for Mobile */}
            <PascoDetailModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                pascoId={selectedPascoId}
            />

            {/* Filter Modal - Only for Mobile */}
            {screenSize === ScreenSize.Mobile && (
                <FilterModal
                    isOpen={isFilterModalOpen}
                    onClose={() => setIsFilterModalOpen(false)}
                    onApplyFilters={applyFilters}
                    initialFilters={{
                        searchQuery: searchQuery,
                        semester: semesterFilter,
                        academicYear: academicYearFilter,
                        university: universityFilter,
                    }}
                    totalResults={totalPascos}
                    filterOptions={filterOptions}
                />
            )}
        </div>
    );
}